import {
  Camera,
  Object3D,
  OrthographicCamera,
  PerspectiveCamera,
  Quaternion,
  Vector3,
} from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { logger } from "../../util/Logger";
import { CameraInterface, Entity } from "../Engine";
import { CAMERA_FAR, CAMERA_NEAR } from "./CameraConstants";
import { fitObjectToCamerInterface, centerAndFitCamera } from "./CameraUtils";

export class EngineCamera extends Entity implements CameraInterface {
  private _type: CameraType = CameraType.Perspective;
  private _viewAngle = 25;
  private _width?;
  private _height?;

  private _cameraOrto?: OrthographicCamera;
  private _cameraPersController?: OrbitControls;

  private _cameraPers?: PerspectiveCamera;
  private _cameraOrtoController?: OrbitControls;

  /** */

  constructor(width?: number, height?: number) {
    super();
    if (width) this._width = width;
    if (height) this._height = height;
    if (!this._width) this._width = this.engine?.width; // Default width if not provided
    if (!this._height) this._height = this.engine?.height; // Default height if not provided

    this._reset();
    this.update();
    this.addEventListener("change", this._cameraChange.bind(this));
    this.addEventListener("end", this._cameraEnd.bind(this));
    this.addEventListener("start", this._cameraStart.bind(this));
  }

  begin(): void {
    if (!this._width) this._width = this.engine!.width;
    if (!this._height) this._height = this.engine!.height;

    this.update();
  }

  set cameraType(value: CameraType) {
    if (this._type != value) {
      this._reset();
      this.update();
    }
    this._type = value;
  }

  get cameraType() {
    return this._type;
  }

  set viewAngle(value: number) {
    this._viewAngle = value;
    this.update();
  }

  get viewAngle() {
    return this._viewAngle;
  }

  set enableRotate(value: boolean) {
    if (this._type == CameraType.Orthographic)
      this._cameraOrtoController!.enableRotate = value;
    if (this._type == CameraType.Perspective)
      this._cameraPersController!.enableRotate = value;
  }

  get controller() {
    return this._type == CameraType.Orthographic
      ? this._cameraOrtoController
      : this._cameraPersController;
  }

  /** Pan so the world point stays centered, then dolly with the existing orbit control. */
  focusWorldPoint(x: number, z: number, direction: 1 | -1): void {
    const controls = this.controller;
    const camera = this.instance;
    if (!controls || !camera) return;
    const next = new Vector3(x, controls.target.y, z);
    const delta = next.clone().sub(controls.target);
    controls.target.add(delta);
    camera.position.add(delta);
    controls.update();
    const scale = 1.15;
    if (direction > 0) controls.dollyIn(scale);
    else controls.dollyOut(scale);
    controls.update();
  }

  unproject(point: Vector3): void {}

  get instance() {
    return this._type == CameraType.Orthographic
      ? this._cameraOrto!
      : this._cameraPers!;
  }

  private _lastCurrentZoom = 0;
  private _currentZoom = 0;
  private _camereEndTimeOut: ReturnType<typeof setTimeout> | null = null;

  getZoomFromCamera(camera: any) {
    if (camera.type == "OrthographicCamera") {
      return camera.zoom;
    } else {
      let zoom = camera.position.distanceTo(camera.userData.controller.target);
      return zoom;
    }
  }

  private _checkCameraChangeEnd(camera: any) {
    if (this._camereEndTimeOut) {
      clearTimeout(this._camereEndTimeOut);
    }

    this._lastCurrentZoom = this.getZoomFromCamera(camera);

    this._camereEndTimeOut = setTimeout(() => {
      this._currentZoom = this.getZoomFromCamera(camera);

      if (Math.abs(this._lastCurrentZoom - this._currentZoom) < 1e-8) {
        this.engine!.dispatchCustomEvent("request-update");
        this.engine!.dispatchCustomEvent("camera-end", { detail: camera });
        logger.debug("camera-end");
      }
    }, 50);
  }

  private _cameraChange(e: any) {
    let camera = e.target.object;
    this.engine!.dispatchCustomEvent("camera-change", { detail: camera });
    this._checkCameraChangeEnd(camera);
  }

  private _cameraStart(e: any) {
    if (this._camereEndTimeOut) clearTimeout(this._camereEndTimeOut);
    this.engine!.dispatchCustomEvent("camera-start", {
      detail: e.target.object,
    });
  }

  private _cameraEnd(e: any) {
    const camera = e.target.object;
    this._checkCameraChangeEnd(camera);
  }

  addEventListener(name: string, func: (event: any) => void) {
    this._cameraOrtoController?.addEventListener(name as any, func);
    this._cameraPersController?.addEventListener(name as any, func);
  }

  removeEventListener(name: string, func: (event: any) => void) {
    this._cameraOrtoController?.removeEventListener(name as any, func);
    this._cameraPersController?.removeEventListener(name as any, func);
  }

  update(width?: number, height?: number) {
    if (width) this._width = width;
    if (height) this._height = height;

    // Kamera ve renderer'ı güncelle
    this._reset();
    if (this.cameraType == CameraType.Perspective) {
      this._cameraPers!.aspect = this._width! / this._height!;
      this._cameraPers!.updateProjectionMatrix();
    }
    if (this.cameraType == CameraType.Orthographic) {
      this._cameraOrto!.left =
        (-this._viewAngle * this._width!) / this._height!;
      this._cameraOrto!.right =
        (this._viewAngle * this._width!) / this._height!;
      this._cameraOrto!.top = this.viewAngle;
      this._cameraOrto!.bottom = -this.viewAngle;
      this._cameraOrto?.updateProjectionMatrix();
    }
  }

  animate(): void {
    if (this._type == CameraType.Orthographic) {
      this._cameraOrtoController?.update();
    }

    if (this._type == CameraType.Perspective)
      this._cameraPersController?.update();
  }

  private _cameraOrthoSaveTransform = false;
  private _cameraPersSaveTransform = false;
  saveTransform() {
    if (this._type == CameraType.Orthographic) {
      this._cameraOrtoController?.saveState();
      this._cameraOrthoSaveTransform = true;
    }

    if (this._type == CameraType.Perspective) {
      this._cameraPersController?.saveState();
      this._cameraPersSaveTransform = true;
    }
  }

  restoreTransform() {
    if (
      this._type == CameraType.Orthographic &&
      this._cameraOrthoSaveTransform
    ) {
      this._cameraOrtoController?.reset();
      this._cameraOrthoSaveTransform = false;
    }

    if (this._type == CameraType.Perspective && this._cameraPersSaveTransform) {
      this._cameraPersController?.reset();
      this._cameraPersSaveTransform = false;
    }
  }

  fit(object: Object3D) {
    if (!object) return undefined;

    const result = fitObjectToCamerInterface(object, this);
    if (!result) return undefined;

    if (this._type == CameraType.Orthographic) {
      this._cameraOrto?.position.copy(result.position);
      this._cameraOrtoController?.target.copy(result.target);
      this._cameraOrtoController?.update();
    } else {
      // Perspective: hedef + gerçek kamera pozisyonu (yalnız position0 yetmez)
      this._cameraPers?.position.copy(result.position);
      this._cameraPersController?.target.copy(result.target);
      this._cameraPersController?.position0.copy(result.position);
      this._cameraPersController?.target0.copy(result.target);
      this._cameraPersController?.update();
    }
    return result;
  }

  /** Önce orbit hedefini merkeze al, sonra fit. */
  centerAndFit(object: Object3D) {
    return centerAndFitCamera(this, object);
  }

  private _reset() {
    if (!this._cameraOrto) {
      this._cameraOrto = new OrthographicCamera(
        -(this._width! / this._height!) * this._viewAngle,
        (this._width! / this._height!) * this._viewAngle,
        this._viewAngle,
        -this._viewAngle,
        -1,
        CAMERA_FAR
      );
    }

    if (this._cameraOrto && !this._cameraOrtoController) {
      this._cameraOrtoController = new OrbitControls(
        this._cameraOrto!,
        this.engine?.canvas
      );
      //this.contructCamera(this._cameraOrtoController);
      this._cameraOrtoController.enableDamping = true; // an animation loop is required when either damping or auto-rotation are enabled
      this._cameraOrtoController.dampingFactor = 0.5;
      this._cameraOrtoController.screenSpacePanning = true;
      this._cameraOrtoController.zoomSpeed = 1;
      this._cameraOrtoController.zoomToCursor = true;
      this._cameraOrtoController.maxZoom = this.viewAngle;
      this._cameraOrtoController.minZoom = 1;
      this._cameraOrtoController.maxPolarAngle = Math.PI / 2;
      this._cameraOrtoController.target.set(0, 0, 0);
      this._cameraOrto.userData = { controller: this._cameraOrtoController };
    }

    if (this._cameraOrtoController)
      this._cameraOrtoController.enabled =
        this.cameraType == CameraType.Orthographic;

    if (!this._cameraPers) {
      this._cameraPers = new PerspectiveCamera(
        this._viewAngle,
        this._width! / this._height!,
        CAMERA_NEAR,
        CAMERA_FAR
      );
      this._cameraPers.position.set(-10, 10, 10);
      this._cameraPers.lookAt(0, 1, 0);
    }
    if (!this._cameraPersController) {
      this._cameraPersController = new OrbitControls(
        this._cameraPers!,
        this.engine?.canvas
      );
      //this.contructCamera(this._cameraPersController);

      this._cameraPersController.enableDamping = true; // an animation loop is required when either damping or auto-rotation are enabled
      this._cameraPersController.dampingFactor = 0.5;
      this._cameraPersController.screenSpacePanning = true;
      this._cameraPersController.zoomSpeed = 1;
      this._cameraPersController.zoomToCursor = true;
      this._cameraPersController.rotateSpeed = 0.5;
      this._cameraPersController.minDistance = 1;
      this._cameraPersController.maxDistance = 25;
      this._cameraPersController.maxPolarAngle = Math.PI / 2;
      this._cameraPersController.target.set(0, 1.0, 0);
      this._cameraPers.userData = { controller: this._cameraPersController };
    }

    if (this._cameraPersController)
      this._cameraPersController.enabled =
        this.cameraType == CameraType.Perspective;
  }

  set cameraPosition(value: Vector3) {
    this._cameraOrto?.position.set(value.x, value.y, value.z);
    this._cameraPers?.position.set(value.x, value.y, value.z);
  }

  get cameraPosition(): Vector3 {
    if (this._type == CameraType.Orthographic)
      return this._cameraOrto!.position;
    if (this._type == CameraType.Perspective) return this._cameraPers!.position;
    return new Vector3(0, 0, 0);
  }

  set cameraLookAt(value: Vector3) {
    this._cameraOrto?.lookAt(value);
    this._cameraPers?.lookAt(value);
    this._cameraPersController?.target.copy(value);
    this._cameraOrtoController?.target.copy(value);
  }

  get fov() {
    if (this._type == CameraType.Orthographic) return this._cameraOrto!.zoom;
    if (this._type == CameraType.Perspective) return this._cameraPers!.fov;
    return 0;
  }

  get aspect() {
    if (this._type == CameraType.Orthographic)
      return this._cameraOrto!.view!.width / this._cameraOrto!.view!.height;
    if (this._type == CameraType.Perspective) return this._cameraPers!.aspect;
    return 0;
  }

  set cameraMinZoom(value: number) {
    if (this._cameraOrtoController)
      this._cameraOrtoController.minDistance = value;
    if (this._cameraPersController)
      this._cameraPersController.minDistance = value;
  }

  set cameraMaxZoom(value: number) {
    if (this._cameraOrtoController)
      this._cameraOrtoController.maxDistance = value;
    if (this._cameraPersController)
      this._cameraPersController.maxDistance = value;
  }

  screenShot() {}

  capturePose(): EngineCameraPoseSnapshot | null {
    if (!this._cameraOrto || !this._cameraPers) return null;
    return {
      type: this._type,
      enableRotateOrtho: this._cameraOrtoController?.enableRotate ?? false,
      enableRotatePers: this._cameraPersController?.enableRotate ?? true,
      ortho: {
        position: this._cameraOrto.position.clone(),
        quaternion: this._cameraOrto.quaternion.clone(),
        zoom: this._cameraOrto.zoom,
        target: this._cameraOrtoController?.target.clone() ?? new Vector3(),
      },
      pers: {
        position: this._cameraPers.position.clone(),
        quaternion: this._cameraPers.quaternion.clone(),
        zoom: this._cameraPers.zoom,
        target: this._cameraPersController?.target.clone() ?? new Vector3(),
      },
    };
  }

  applyPose(pose: EngineCameraPoseSnapshot): void {
    this.cameraType = pose.type;
    if (this._cameraOrto) {
      this._cameraOrto.position.copy(pose.ortho.position);
      this._cameraOrto.quaternion.copy(pose.ortho.quaternion);
      this._cameraOrto.zoom = pose.ortho.zoom;
      this._cameraOrto.updateProjectionMatrix();
    }
    if (this._cameraOrtoController) {
      this._cameraOrtoController.target.copy(pose.ortho.target);
      this._cameraOrtoController.enableRotate = pose.enableRotateOrtho;
      this._cameraOrtoController.update();
    }
    if (this._cameraPers) {
      this._cameraPers.position.copy(pose.pers.position);
      this._cameraPers.quaternion.copy(pose.pers.quaternion);
      this._cameraPers.zoom = pose.pers.zoom;
      this._cameraPers.updateProjectionMatrix();
    }
    if (this._cameraPersController) {
      this._cameraPersController.target.copy(pose.pers.target);
      this._cameraPersController.enableRotate = pose.enableRotatePers;
      this._cameraPersController.update();
    }
    this.enableRotate =
      pose.type === CameraType.Orthographic
        ? pose.enableRotateOrtho
        : pose.enableRotatePers;
  }
}

export type EngineCameraPoseSnapshot = {
  type: CameraType;
  enableRotateOrtho: boolean;
  enableRotatePers: boolean;
  ortho: EngineCameraEyePose;
  pers: EngineCameraEyePose;
};

type EngineCameraEyePose = {
  position: Vector3;
  quaternion: Quaternion;
  zoom: number;
  target: Vector3;
};

export class OrthoCamera implements CameraInterface {
  private _camera: OrthographicCamera;
  private _viewAngle = 25;
  private _aspect = 1;

  constructor(fov: number, width: number, height: number) {
    this._aspect = width / height;
    this._viewAngle = fov;
    this._camera = new OrthographicCamera(
      -this._viewAngle * this._aspect * 0.5,
      this._viewAngle * this._aspect * 0.5,
      this._viewAngle * 0.5,
      -this._viewAngle * 0.5,
      CAMERA_NEAR,
      CAMERA_FAR
    );
  }

  addEventListener(name: string, func: (event: any) => void): void {
    throw new Error("Method not implemented.");
  }

  removeEventListener(name: string, func: (event: any) => void): void {
    throw new Error("Method not implemented.");
  }

  get instance(): Camera {
    return this._camera;
  }

  get controller(): any {
    throw new Error("Method not implemented.");
  }

  update(width?: number, height?: number): void {
    this._aspect = width! / height!;
    this._camera!.left = -this._viewAngle * this._aspect * 0.5;
    this._camera!.right = this._viewAngle * this._aspect * 0.5;
    this._camera!.top = this._viewAngle * 0.5;
    this._camera!.bottom = -this._viewAngle * 0.5;
    this._camera?.updateProjectionMatrix();
  }

  fit(object: Object3D | { center: Vector3; radius: number }) {
    const result = fitObjectToCamerInterface(object, this);
    return result;
  }

  unproject(point: Vector3): void {
    throw new Error("Method not implemented.");
  }
}

export class PersCamera implements CameraInterface {
  private _camera: PerspectiveCamera;
  private _viewAngle = 5;
  private _aspect = 1;
  private _controls?: OrbitControls;

  constructor(fov: number, width: number, height: number) {
    this._aspect = width / height;
    this._viewAngle = fov;
    this._camera = new PerspectiveCamera(
      this._viewAngle,
      this._aspect,
      CAMERA_NEAR,
      CAMERA_FAR
    );
  }

  addEventListener(name: string, func: (event: any) => void): void {
    this._controls?.addEventListener(name as any, func);
  }

  removeEventListener(name: string, func: (event: any) => void): void {
    this._controls?.removeEventListener(name as any, func);
  }

  get instance(): Camera {
    return this._camera;
  }

  get controller(): any {
    return this._controls;
  }

  set controller(value: any) {
    this._controls = value;
  }

  update(width?: number, height?: number): void {
    this._aspect = width! / height!;
    this._camera!.aspect = this._aspect;

    this._camera?.updateProjectionMatrix();
  }

  fit(object: Object3D | { center: Vector3; radius: number }) {
    const result = fitObjectToCamerInterface(object, this);
    return result;
  }

  unproject(point: Vector3): void {
    throw new Error("Method not implemented.");
  }
}

export enum CameraType {
  Orthographic,
  Perspective,
}
